import { QRCodeSVG } from "qrcode.react";
import { useTranslation } from "react-i18next";

import { CopyButton } from "@/shared/ui";

import type { DepositPayment } from "../model/types";

/**
 * Адрес, точная сумма и QR — всё, что нужно, чтобы отправить перевод.
 *
 * **QR кодирует голый адрес, а не URI вида `tron:...?amount=`.** Схемы у
 * каждой сети свои, поддержаны кошельками неравномерно, и кошелёк, молча
 * проигнорировавший параметр `amount`, отправит другую сумму — то есть
 * недоплату. Адрес понимают все, а сумма стоит рядом отдельной строкой, где
 * её видно и можно скопировать.
 *
 * **MEMO по той же причине не зашивается в QR, а выносится в отдельный блок с
 * предупреждением.** Платёж без memo провайдер не находит автоматически, и
 * спрятать его в параметре URI значит поставить на то, что кошелёк этот
 * параметр прочитает.
 */
export function PayAddressCard({ payment }: { payment: DepositPayment }) {
  const { t } = useTranslation();
  const currency = payment.pay_currency.toUpperCase();

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-border-subtle bg-background-elevated p-4">
      <div className="flex flex-col gap-1">
        <span className="text-caption font-medium text-text-secondary">
          {t("wallet.payment.sendExactly")}
        </span>
        <div className="flex items-center gap-3">
          <span className="min-w-0 flex-1 font-mono text-h3 font-bold break-all text-text-primary">
            {payment.pay_amount} {currency}
          </span>
          <CopyButton value={payment.pay_amount} />
        </div>
      </div>

      <div className="flex justify-center">
        {/* Белая подложка обязательна: сканеры читают тёмный код на светлом,
            а в тёмной теме элемент иначе сливается с фоном. */}
        <div className="rounded-xl bg-white p-3">
          <QRCodeSVG
            value={payment.pay_address}
            size={168}
            level="M"
            title={t("wallet.payment.qrAlt")}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <span className="text-caption font-medium text-text-secondary">
          {t("wallet.address")}
        </span>
        <div className="flex items-center gap-3">
          {/* Адрес виден целиком и доступен для выделения: копирование в
              Telegram WebView срабатывает не на всех клиентах. */}
          <span className="min-w-0 flex-1 font-mono text-caption break-all text-text-primary">
            {payment.pay_address}
          </span>
          <CopyButton value={payment.pay_address} />
        </div>
      </div>

      {payment.payin_extra_id ? (
        <div className="flex flex-col gap-2 rounded-xl bg-surface-muted p-3">
          <span className="text-caption font-medium text-text-secondary">
            {t("wallet.payment.memo")}
          </span>
          <div className="flex items-center gap-3">
            <span className="min-w-0 flex-1 font-mono text-caption break-all text-text-primary">
              {payment.payin_extra_id}
            </span>
            <CopyButton value={payment.payin_extra_id} />
          </div>
          <p className="text-caption font-normal text-status-warning">
            {t("wallet.payment.memoWarning")}
          </p>
        </div>
      ) : null}

      <p className="text-caption font-normal text-status-warning">
        {t("wallet.networkWarning")}
      </p>
    </section>
  );
}
